import { useCallback, useEffect, useState } from "react"

/**
 * Estado de navegación en el QUERY STRING de un solo path.
 * `/dashboard?month=2026-08`
 *
 * Portado del tablero de reuniones. Reemplaza a un router, y es una decisión de
 * arquitectura, no de conveniencia:
 *
 *  1. EL PATH NUNCA CAMBIA, así que Express no necesita ninguna ruta comodín para el
 *     history fallback. Y sin comodines, nada puede tapar POST /slack/events (que
 *     dejaría el bot muerto con /health en verde) ni /health. Además `router.get("*")`
 *     TIRA al bootear en Express 5 con path-to-regexp v8. Ver src/http.ts.
 *  2. EL HASH QUEDA RESERVADO para los links de recovery de Supabase
 *     (#access_token=…&type=recovery). Un HashRouter intentaría matchear eso como ruta
 *     y rompería el reset de contraseña — por eso NUNCA hay que adoptar uno acá.
 *  3. El mes elegido pasa a ser un LINK COMPARTIBLE y a prueba de refresh: mandar
 *     "las horas de julio" por Slack es pegar una URL.
 */
export type QueryState = Record<string, string | undefined>

/**
 * Lo que devuelve el hook, exportado para tipar los props de las páginas: el hook
 * se instancia UNA SOLA VEZ (en App) y baja por props. No es estilo: cada
 * instancia tiene su propio useState y pushState no dispara popstate, así que dos
 * instancias no se ven entre sí — la segunda escribiría la URL completa desde su
 * copia vieja y pisaría el ?month= que la primera acaba de setear.
 */
export interface QueryStateApi {
  query: QueryState
  setQuery: (patch: QueryState, opts?: { replace?: boolean }) => void
}

function read(): QueryState {
  const out: QueryState = {}
  new URLSearchParams(window.location.search).forEach((v, k) => {
    out[k] = v
  })
  return out
}

function write(next: QueryState, replace: boolean): void {
  const params = new URLSearchParams()
  for (const [k, v] of Object.entries(next)) {
    if (v !== undefined && v !== "") params.set(k, v)
  }
  const qs = params.toString()
  // Se preserva el pathname tal cual (puede ser /dashboard o /dashboard/) y el hash,
  // que puede traer el token de recovery de Supabase.
  const url = `${window.location.pathname}${qs ? `?${qs}` : ""}${window.location.hash}`
  if (replace) window.history.replaceState(null, "", url)
  else window.history.pushState(null, "", url)
}

export function useQueryState(): QueryStateApi {
  const [query, setQueryState] = useState<QueryState>(read)

  // El botón atrás del browser tiene que funcionar: es la forma natural de deshacer
  // un cambio de mes.
  useEffect(() => {
    const onPop = () => setQueryState(read())
    window.addEventListener("popstate", onPop)
    return () => window.removeEventListener("popstate", onPop)
  }, [])

  const setQuery = useCallback((patch: QueryState, opts?: { replace?: boolean }) => {
    setQueryState((prev) => {
      const next = { ...prev, ...patch }
      for (const k of Object.keys(next)) {
        if (next[k] === undefined || next[k] === "") delete next[k]
      }
      write(next, opts?.replace ?? false)
      return next
    })
  }, [])

  return { query, setQuery }
}
